import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import CircularProgress from '@mui/material/CircularProgress'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import RateReviewIcon from '@mui/icons-material/RateReview'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'

import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import ResourceTagsSection from '../shared/ResourceTagsSection'
import { initializeTags } from '../../reducers/tagsReducers'
import { updateProposal, swapPrimaryCandidate } from '../../reducers/automationReducer'
import { safeHref } from '../../utils/safeUrl'

// Plain-text candidate fields, rendered only when present on the blob (candidates are
// Paper-shaped today, but proposed_data is polymorphic across resource types).
const TEXT_FIELDS = [
    { key: 'title', label: 'Title' },
    { key: 'authors', label: 'Authors' },
    { key: 'journal', label: 'Journal' },
    { key: 'link', label: 'Link' },
    { key: 'pub_date', label: 'Publication date (YYYY-MM-DD)' },
]

// proposed_data field → tag-store type, matching the selectors used by the other admin modals.
const TAG_FIELDS = [
    { key: 'content_tags', tagType: 'content' },
    { key: 'medical_specialty_tags', tagType: 'medicalSpecialty' },
    { key: 'admin_category_tags', tagType: 'adminCategory' },
    { key: 'experience_tags', tagType: 'experience' },
    { key: 'demographic_tags', tagType: 'demographic' },
]

// Map raw proposed_data tag names onto the loaded taxonomy: case variants fold onto the
// canonical name, names the taxonomy doesn't know are reported as dropped. TagSelector can
// only offer taxonomy names, and an unknown name stuck in its Select could never be removed.
const canonicalizeTags = (names, taxonomy) => {
    const canonicalByFold = new Map((taxonomy || []).map(tag => [tag.name.toLowerCase(), tag.name]))
    const known = []
    const dropped = []
    for (const raw of names || []) {
        const canonical = canonicalByFold.get(String(raw).toLowerCase())
        if (canonical) {
            if (!known.includes(canonical)) known.push(canonical)
        } else {
            dropped.push(raw)
        }
    }
    return { known, dropped }
}

// Review a pending change proposal's candidate metadata — and edit it in place — before
// approving. Approval only lives inside this dialog, so an admin always looks the candidate
// over first; unsaved edits are saved as part of approving. Fields not shown here
// (resourcetype, doi, …) pass through unchanged; the backend re-validates the blob on save.
const EditProposalModal = ({ proposal, onSaved, onApprove }) => {
    const dispatch = useDispatch()
    const tagsByType = useSelector(state => state.tags)

    const [open, setOpen] = useState(false)
    const [form, setForm] = useState({})
    const [tagSelections, setTagSelections] = useState({})
    const [droppedTags, setDroppedTags] = useState([])
    // Once the admin edits any tag list, taxonomy refreshes must not clobber their selection.
    const [tagsTouched, setTagsTouched] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const [swapped, setSwapped] = useState(null)  // server payload after a swap; wins over the prop
    const [swappingId, setSwappingId] = useState(null)  // id of the candidate mid-swap
    const swapping = swappingId !== null

    const current = swapped || proposal
    const data = current.proposed_data || {}
    const alternatives = (current.candidates || []).filter(
        (candidate) => candidate.id !== current.primary_candidate
    )
    const textFields = TEXT_FIELDS.filter(({ key }) => key in data)

    // Preload the five taxonomies so canonicalization has data by the time the dialog opens
    // (initializeTags no-ops for types already in the store).
    useEffect(() => {
        TAG_FIELDS.forEach(({ tagType }) => dispatch(initializeTags(tagType)))
    }, [dispatch])

    // Canonicalize every tag list on a blob against the loaded taxonomies in one pass.
    const deriveTagSelections = (blob) => {
        const selections = {}
        const dropped = []
        TAG_FIELDS.forEach(({ key, tagType }) => {
            const result = canonicalizeTags(blob[key], tagsByType?.[tagType])
            selections[key] = result.known
            dropped.push(...result.dropped)
        })
        return { selections, dropped }
    }

    // (Re)derive the tag selections from the blob while the dialog is open and untouched —
    // covers opening the dialog before a taxonomy fetch has landed.
    useEffect(() => {
        if (!open || tagsTouched) return
        const { selections, dropped } = deriveTagSelections(data)
        setTagSelections(selections)
        setDroppedTags(dropped)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, tagsTouched, tagsByType, current])

    const initForm = (blob) => {
        const next = {}
        TEXT_FIELDS.filter(({ key }) => key in blob).forEach(({ key }) => {
            next[key] = blob[key] ?? ''
        })
        next.description = blob.description ?? ''
        if ('abstract' in blob) next.abstract = blob.abstract ?? ''
        setForm(next)
        setTagsTouched(false)
        setDirty(false)
    }

    const handleOpen = () => {
        setSwapped(null)
        // Init from the prop explicitly: after the reset above, the tag-derivation
        // effect also reads the prop, so both halves of the dialog share one blob.
        const blob = proposal.proposed_data || {}
        initForm(blob)
        // Seed the selections here too, not just in the effect above: the effect only runs
        // after open has flipped, so an already-tagged proposal would otherwise paint its
        // first frame with Approve disabled and the red demographic hint showing.
        const { selections, dropped } = deriveTagSelections(blob)
        setTagSelections(selections)
        setDroppedTags(dropped)
        setOpen(true)
    }

    const handleClose = () => setOpen(false)

    const setField = (key) => (event) => {
        setDirty(true)
        setForm({ ...form, [key]: event.target.value })
    }

    const setTagField = (key) => (names) => {
        setTagsTouched(true)
        setDirty(true)
        setTagSelections({ ...tagSelections, [key]: names })
    }

    // Issue #473: the backend refuses to publish an ADD/REPLACE whose demographic_tags is
    // empty — automation never derives one, so it has to be chosen here. Blocking the button
    // makes that a visible precondition instead of a failed approve round-trip. REMOVE
    // proposals are untouched: they never open this dialog (ManageAutomation.js:715).
    const missingDemographicTags = (tagSelections.demographic_tags || []).length === 0

    const buildEdited = () => {
        const edited = { ...data }
        textFields.forEach(({ key }) => { edited[key] = form[key] })
        edited.description = form.description
        if ('abstract' in data) edited.abstract = form.abstract
        TAG_FIELDS.forEach(({ key }) => { edited[key] = tagSelections[key] || [] })
        return edited
    }

    // Persist current edits (dropped off-taxonomy tags count as an edit even when the admin
    // typed nothing). Returns whether the proposal is now safe to act on.
    const saveEdits = async () => {
        if (!dirty && droppedTags.length === 0) return true
        return await dispatch(updateProposal(proposal.id, buildEdited()))
    }

    const handleSave = async () => {
        setSaving(true)
        const ok = await dispatch(updateProposal(proposal.id, buildEdited()))
        setSaving(false)
        if (ok) {
            handleClose()
            if (onSaved) onSaved()
        }
    }

    // Approve = save any pending edits first, then apply. A failed save (validation error)
    // aborts the approval so the admin can fix the field it named.
    const handleApprove = async () => {
        setSaving(true)
        const ok = await saveEdits()
        setSaving(false)
        if (!ok) return
        handleClose()
        if (onApprove) await onApprove()
    }

    // Make an alternative the primary: the backend rebuilds proposed_data from that
    // candidate's raw source data, so any unsaved edits here are discarded.
    const handleSwap = (candidate) => async () => {
        const hasEdits = dirty || droppedTags.length > 0
        if (hasEdits && !window.confirm(
            'Making this candidate primary rebuilds the proposal and discards your unsaved edits. Continue?'
        )) {
            return
        }
        setSwappingId(candidate.id)
        const updated = await dispatch(swapPrimaryCandidate(proposal.id, candidate.id))
        setSwappingId(null)
        if (updated) {
            // Bind the rebuilt blob once and seed both halves from it, same as handleOpen: the
            // tag-derivation effect only runs after `current` has actually changed, so without
            // this the first frame after a swap would still show Approve enabled off the
            // pre-swap tagSelections. Read from the parameter, never from `swapped` — the
            // setSwapped call below has not committed yet.
            const blob = updated.proposed_data || {}
            setSwapped(updated)
            initForm(blob)
            const { selections, dropped } = deriveTagSelections(blob)
            setTagSelections(selections)
            setDroppedTags(dropped)
            if (onSaved) onSaved()
        }
    }

    return (
        <>
            <Button variant='outlined' size='small' onClick={handleOpen}>
                <RateReviewIcon fontSize='small' />&nbsp;Review
            </Button>
            <BootstrapDialog
                onClose={handleClose}
                aria-labelledby={`edit-proposal-${proposal.id}`}
                open={open}
                fullWidth
                maxWidth='md'
            >
                <BootstrapDialogTitle id={`edit-proposal-${proposal.id}`} onClose={handleClose}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EditIcon sx={{ marginRight: '10px' }} />
                        Review Proposed {data.resourcetype || 'Resource'}
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                        Look the candidate over and fix its metadata if needed, then approve
                        to publish. Edits are validated on save.
                    </Typography>
                    {droppedTags.length > 0 && (
                        <Alert severity='warning' sx={{ marginBottom: '10px' }}>
                            These proposed tags are not in the hub and will be
                            removed on save: {droppedTags.join(', ')}
                        </Alert>
                    )}
                    {textFields.map(({ key, label }) => (
                        <TextField
                            key={key}
                            label={label}
                            value={form[key] ?? ''}
                            onChange={setField(key)}
                            fullWidth
                            margin='dense'
                        />
                    ))}
                    <TextField
                        label='Description'
                        value={form.description ?? ''}
                        onChange={setField('description')}
                        fullWidth
                        multiline
                        minRows={4}
                        margin='dense'
                    />
                    {'abstract' in data && (
                        <TextField
                            label='Abstract'
                            value={form.abstract ?? ''}
                            onChange={setField('abstract')}
                            fullWidth
                            multiline
                            minRows={4}
                            margin='dense'
                        />
                    )}
                    <Grid container spacing={2} sx={{ marginTop: '2px' }}>
                        <ResourceTagsSection
                            contentTags={tagSelections.content_tags || []}
                            setContentTags={setTagField('content_tags')}
                            medicalTags={tagSelections.medical_specialty_tags || []}
                            setMedicalTags={setTagField('medical_specialty_tags')}
                            adminCategoryTags={tagSelections.admin_category_tags || []}
                            setAdminCategoryTags={setTagField('admin_category_tags')}
                            experienceTags={tagSelections.experience_tags || []}
                            setExperienceTags={setTagField('experience_tags')}
                            demographicTags={tagSelections.demographic_tags || []}
                            setDemographicTags={setTagField('demographic_tags')}
                        />
                    </Grid>
                    {alternatives.length > 0 && (
                        <Box sx={{ marginTop: '16px' }}>
                            <Typography variant='subtitle1'>Alternative candidates</Typography>
                            <Typography variant='body2' color='text.secondary' gutterBottom>
                                Other candidates scored this round, best first. Making one
                                primary rebuilds the proposal from it.
                            </Typography>
                            {alternatives.map((candidate) => {
                                const candidateHref = safeHref(candidate.data?.link)
                                return (
                                <Box
                                    key={candidate.id}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: '10px',
                                        padding: '6px 0', borderTop: '1px solid', borderColor: 'divider',
                                    }}
                                >
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                        {candidateHref ? (
                                            <Link href={candidateHref} target='_blank' rel='noopener noreferrer'>
                                                {candidate.data?.title || candidate.data?.doi || 'Untitled candidate'}
                                            </Link>
                                        ) : (
                                            <Typography component='span'>
                                                {candidate.data?.title || 'Untitled candidate'}
                                            </Typography>
                                        )}
                                        <Typography variant='body2' color='text.secondary'>
                                            {[candidate.data?.journal, candidate.data?.pub_date]
                                                .filter(Boolean).join(' — ')}
                                        </Typography>
                                        {candidate.rationale && (
                                            <Typography variant='body2' color='text.secondary'>
                                                {candidate.rationale}
                                            </Typography>
                                        )}
                                    </Box>
                                    {candidate.score != null && (
                                        <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                                            Score: {candidate.score.toFixed(2)}
                                        </Typography>
                                    )}
                                    <Button
                                        size='small'
                                        variant='outlined'
                                        disabled={swapping || saving}
                                        onClick={handleSwap(candidate)}
                                        aria-label={`Make ${candidate.data?.title || 'candidate'} primary`}
                                        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                                    >
                                        {swappingId === candidate.id
                                            ? <CircularProgress size={16} color='inherit' />
                                            : <SwapHorizIcon fontSize='small' />}
                                        &nbsp;Make primary
                                    </Button>
                                </Box>
                                )
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {missingDemographicTags && (
                        <Typography variant='body2' color='error' sx={{ marginRight: 'auto' }}>
                            Select at least one Demographic tag to approve.
                        </Typography>
                    )}
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant='outlined' onClick={handleSave} disabled={saving || swapping || !dirty}>
                        Save changes
                    </Button>
                    <Button
                        variant='contained'
                        color='success'
                        onClick={handleApprove}
                        disabled={saving || swapping || missingDemographicTags}
                    >
                        <CheckIcon fontSize='small' />&nbsp;Approve
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </>
    )
}

export default EditProposalModal
